import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { colors } from "../../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import {
  LoadingSpinner,
  MiniAccount,
  NotificationModal,
} from "../../allComponents/AllComponents";
import { Form, Formik } from "formik";
import { reviewValidation } from "../../../utility/validation/reviewValidation";
import { IReviewPayload } from "../../../utility/types/reviewType";
import { useEffect, useState } from "react";
import {
  reviewError,
  reviewIsLoading,
  selectUserReview,
} from "../../../redux/selectors/reviewSelector";
import { AppDispatch } from "../../../redux/store/store";
import {
  createReview,
  getAllReviews,
  getUserReview,
} from "../../../redux/actions/reviewAction";
import { useNavigate } from "react-router-dom";
import { StyledTypography } from "../../circle-leader-application/styles";
interface ReviewModalProps {
  open: boolean;
  handleClose: () => void;
}

const ReviewModal = (props: ReviewModalProps) => {
  const { open, handleClose } = props;

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const userData = useSelector(selectCurrentUser);

  const userReview = useSelector(selectUserReview);

  const isError = useSelector(reviewError);

  const isLoading = useSelector(reviewIsLoading);

  const [errorAlert, setErrorAlert] = useState("");

  const [openNotification, setOpenNotification] = useState(false);

  const initialValues: IReviewPayload = {
    user: userData?._id ?? "",
    description: "",
    ratings: 0,
  };

  const handleSubmit = async (values: IReviewPayload) => {
    try {
      if (!userData?._id) {
        setErrorAlert("No User ID. Cannot submit review.");
        return;
      }

      const payload = {
        user: userData?._id,
        description: values.description,
        ratings: values.ratings,
      };

      const data = await dispatch(createReview(payload)).unwrap();
      dispatch(getAllReviews());

      if (data._id) {
        setOpenNotification(true);

        setTimeout(() => {
          setOpenNotification(false);
          handleClose();
          navigate(`/user/${userData?._id}`);
        }, 5000);
      } else {
        setOpenNotification(false);
        setErrorAlert(isError);
      }
    } catch (error: any) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    if (userData?._id) {
      dispatch(getUserReview(userData?._id));
    }
  }, [dispatch, userData?._id]);

  const handleCloseErrorAlert = () => {
    setErrorAlert("");
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        {errorAlert && (
          <Stack sx={{ width: "100%" }} spacing={2}>
            <Alert severity="error" onClose={handleCloseErrorAlert}>
              Error -{" "}
              <strong>
                {errorAlert
                  ? errorAlert
                  : "Failed to share review. Unknown error occurred. Please try later"}
              </strong>
            </Alert>
          </Stack>
        )}
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.white,
              backgroundColor: colors.cancel,
              "&:hover": {
                color: colors.white,
                backgroundColor: colors.cancel,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {!userReview && (
            <Typography
              component="h1"
              variant="h4"
              align="center"
              sx={{ mb: 0 }}
            >
              Share Your Experience
            </Typography>
          )}
        </DialogContent>
        <Container
          maxWidth="lg"
          sx={{
            mt: 7,
          }}
        >
          <Stack spacing={8} sx={{ width: "100%" }}>
            <div>
              <Grid container spacing={5}>
                <Grid item xs={12} md={6} lg={4} sx={{ padding: 2 }}>
                  <MiniAccount
                    name={userData?.firstName ?? ""}
                    image={userData?.profileImage ?? ""}
                    email={userData?.email ?? ""}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={8} sx={{ padding: 2 }}>
                  {userReview ? (
                    <Typography
                      color="text.secondary"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 25,
                      }}
                    >
                      You already submitted a review. Members can only leave one
                      review.
                    </Typography>
                  ) : (
                    <Formik
                      initialValues={initialValues}
                      onSubmit={handleSubmit}
                      validation={reviewValidation}
                    >
                      {({ values, handleChange, errors, touched }) => (
                        <Form>
                          <Card>
                            <CardHeader
                              subheader="Please provide details about your experience. Your feedback is valuable to us."
                              title="Leave A Review."
                            />
                            <CardContent>
                              <Box sx={{ m: 5 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={12}>
                                    <TextField
                                      fullWidth
                                      label="share...."
                                      name="description"
                                      multiline
                                      rows={4}
                                      onChange={handleChange}
                                      value={values.description}
                                      error={
                                        !!(
                                          errors?.description &&
                                          touched?.description
                                        )
                                      }
                                      helperText={
                                        errors.description &&
                                        touched.description &&
                                        errors.description
                                      }
                                      sx={{
                                        fontSize: "1.2rem",
                                        "& .MuiOutlinedInput-root": {
                                          "& fieldset": {
                                            borderColor: "black",
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "black",
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "black",
                                          },
                                        },
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        "& > * + *": {
                                          marginTop: 2,
                                        },
                                      }}
                                    >
                                      <Rating
                                        name="ratings"
                                        value={values.ratings}
                                        onChange={(event, newValue) => {
                                          handleChange({
                                            target: {
                                              name: "ratings",
                                              value: newValue,
                                            },
                                          });
                                        }}
                                        precision={0.5} // Allows for half-star ratings; remove this line if you want only full stars
                                        size="large" // Makes the stars larger; remove or adjust as needed
                                      />
                                      {errors.ratings && touched.ratings && (
                                        <Typography color="error">
                                          {errors.ratings}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </CardContent>
                            <Divider sx={{ backgroundColor: "black" }} />
                            <CardActions sx={{ justifyContent: "center" }}>
                              <Button
                                disabled={!!userReview}
                                variant="contained"
                                type="submit"
                                sx={{
                                  color: colors.white,
                                  backgroundColor: colors.secondPrimary,
                                  "&:hover": {
                                    backgroundColor: colors.secondPrimary,
                                  },
                                  width: 150,
                                }}
                              >
                                {isLoading ? (
                                  <LoadingSpinner
                                    loading={isLoading}
                                    size={35}
                                    color={colors.white}
                                  />
                                ) : (
                                  "Share"
                                )}
                              </Button>
                            </CardActions>
                          </Card>
                        </Form>
                      )}
                    </Formik>
                  )}
                </Grid>
              </Grid>
            </div>
          </Stack>
        </Container>
      </Dialog>

      {openNotification && (
        <NotificationModal
          open={openNotification}
          handleClose={() => setOpenNotification(false)}
        >
          <StyledTypography
            sx={{ fontSize: "1.1rem", color: "text.secondary" }}
          >
            You have successfully shared your experience. Thank you for your
            feedback.
          </StyledTypography>
        </NotificationModal>
      )}
    </>
  );
};

ReviewModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default ReviewModal;
